import React from 'react';
import { useQuery } from '@apollo/client/react';
import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import GET_SUB_ACCOUNTS_FOR_ACCOUNT from '../gql/getSubAccountsForAccount.gql';

import { Table } from '@repo/ui/Table';
import AccountsTableFilter from './SubAccountsTableFilter';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { z } from 'zod';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Form } from '@repo/ui/Form';
import { ButtonLink } from '@repo/ui/Button';
import { Link } from 'react-router';
import { truncateName } from '@/lib/truncateName';
import { SelectInputOptions } from '@repo/types/Types';
import { getNonEmpty } from '@repo/util/nonEmpty';

const SORT_OPTIONS: SelectInputOptions = [
  { label: 'Default', value: '' },
  { label: 'Name (A–Z)', value: 'name_asc' },
  { label: 'Name (Z–A)', value: 'name_desc' },
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
] as const;

const ORDER_BY: Record<string, { key: string; direction: 'ASC' | 'DESC' }> = {
  name_asc: { key: 'name', direction: 'ASC' },
  name_desc: { key: 'name', direction: 'DESC' },
  newest: { key: 'inserted_at', direction: 'DESC' },
  oldest: { key: 'inserted_at', direction: 'ASC' },
};

const SUB_ACCOUNTS_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  name: z.string().default(''),
  customer_id: z.string().default(''),
  sort: z.enum(getNonEmpty(SORT_OPTIONS.map((option) => option.value))).default(''),
});

const PAGE_SIZE = 10;

export interface SubAccountsTableProps {
  accountId: string;
  maxAccountSubAccountsCoun?: number;
}

const SubAccountsTable = ({
  accountId,
  // maxAccountSubAccountsCount,
}: SubAccountsTableProps) => {
  const user = useUser();
  const canUpdateSubAccounts = POLICIES['accounts:update'](user);

  const filter = useZodQuery({
    schema: SUB_ACCOUNTS_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, sort, ...queryFields } = filter.queryValues;

  const orderBy = ORDER_BY[sort];

  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
      },
      coerceValue,
      ['name', 'customer_id']
    ),
  ];

  const { loading, error, data } = useQuery(GET_SUB_ACCOUNTS_FOR_ACCOUNT, {
    variables: {
      id: accountId,
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      ...(orderBy ? { orderBy: [orderBy] } : {}),
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    console.error(error);
  }

  const { edges: accountNodes, pageInfo } = data?.subAccountsForAccount ?? {};

  return (
    <div>
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <AccountsTableFilter SORT_OPTIONS={SORT_OPTIONS} />
      </Form>
      {/* {maxAccountSubAccountsCount != null ? (
        <div className="mb-1">
          <strong>Max number of permitted sub accounts:</strong>{' '}
          {maxAccountSubAccountsCount === -1 ? 'Unlimited' : maxAccountSubAccountsCount}
        </div>
      ) : null} */}
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={accountNodes?.map((account) => account.node) ?? []}
        dataType="accounts"
        columns={[
          {
            title: 'Status',
            width: 'md',
            Cell({ entry: { deactivatedAt } }) {
              return (
                <div className="flex relative gap-2 items-center">
                  <div
                    className={`h-2.5 w-2.5 rounded-full ${deactivatedAt ? 'bg-red-500' : 'bg-green-500'}`}
                  ></div>
                  <span className="">{deactivatedAt ? 'Deactivated' : 'Active'}</span>
                </div>
              );
            },
          },
          {
            title: 'Name',
            Cell({ entry: { name, id, customerId } }) {
              return (
                <div className="flex flex-col gap-1">
                  <span title={name}>{truncateName(name)}</span>

                  <span className="text-sm dark:text-grey-400 text-grey-600">
                    ID: {customerId?.toString()}
                  </span>
                </div>
              );
            },
          },
          {
            title: 'API Access',
            Cell({ entry: { smsAccess, mmsAccess, lrnAccess } }) {
              return (
                <div className="flex gap-10 justify-between items-end w-min">
                  <div className="flex flex-col gap-1 items-center">
                    <span
                      className={`material-symbols-outlined ${
                        smsAccess
                          ? 'dark:text-blue-100 text-grey-1000'
                          : 'dark:text-grey-600 text-grey-400'
                      }`}
                    >
                      sms
                    </span>
                    <p className="text-xsm dark:text-gray-400">SMS</p>
                  </div>
                  <div className="flex flex-col gap-1 items-center">
                    <span
                      className={`material-symbols-outlined ${
                        mmsAccess
                          ? 'dark:text-blue-100 text-grey-1000'
                          : 'dark:text-grey-600 text-grey-400'
                      }`}
                    >
                      mms
                    </span>
                    <p className="text-xsm dark:text-gray-400">MMS</p>
                  </div>
                </div>
              );
            },
          },
          {
            title: canUpdateSubAccounts ? 'Update' : '',
            width: canUpdateSubAccounts ? 'sm' : 'hidden',
            Cell({ entry: { id } }) {
              return canUpdateSubAccounts ? (
                <ButtonLink
                  as={Link}
                  size="sm"
                  variant="secondary"
                  icon="edit"
                  href={`/a/${accountId}/sub-accounts/${id}`}
                >
                  Edit
                </ButtonLink>
              ) : (
                <></>
              );
            },
          },
          {
            title: 'Manage',
            width: 'sm',
            Cell({ entry: { id } }) {
              return (
                <ButtonLink
                  as={Link}
                  size="sm"
                  variant="secondary"
                  icon="mystery"
                  href={`/a/${id}`}
                >
                  Manage
                </ButtonLink>
              );
            },
          },
        ]}
        error={!!error}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default SubAccountsTable;
